import { useEffect, useRef, useState } from 'react';

/**
 * Finger/stylus/mouse signature on a canvas. Calls `onChange` with a PNG
 * (transparent background, dark ink) after each stroke, or null when cleared.
 * Sized to the device's pixel ratio so it stays sharp; ignores page scroll
 * while signing.
 */
export function SignaturePad({ onChange, label = 'Sign above', height = 170 }: { onChange: (png: Blob | null) => void; label?: string; height?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = ref.current!;
    const fit = () => {
      const r = c.getBoundingClientRect();
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      c.width = Math.round(r.width * dpr);
      c.height = Math.round(height * dpr);
      const g = c.getContext('2d')!;
      g.scale(dpr, dpr);
      g.lineCap = 'round';
      g.lineJoin = 'round';
      g.lineWidth = 2.4;
      g.strokeStyle = '#10213a';
      setEmpty(true);
      onChange(null);
    };
    fit();
    const ro = new ResizeObserver(() => { if (Math.abs(c.getBoundingClientRect().width * (window.devicePixelRatio || 1) - c.width) > 2) fit(); });
    ro.observe(c);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  const pos = (e: React.PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e: React.PointerEvent) => {
    e.preventDefault();
    ref.current!.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = pos(e);
    const g = ref.current!.getContext('2d')!;
    g.beginPath();
    g.arc(last.current.x, last.current.y, 1.2, 0, Math.PI * 2);
    g.fillStyle = '#10213a';
    g.fill();
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current || !last.current) return;
    const p = pos(e);
    const g = ref.current!.getContext('2d')!;
    g.beginPath();
    g.moveTo(last.current.x, last.current.y);
    g.lineTo(p.x, p.y);
    g.stroke();
    last.current = p;
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    setEmpty(false);
    ref.current!.toBlob((b) => onChange(b), 'image/png');
  };
  const clear = () => {
    const c = ref.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    onChange(null);
  };

  return (
    <div className="sig-wrap">
      <canvas ref={ref} className="sig-pad" style={{ height }} aria-label="Signature pad — sign with your finger"
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up} />
      <div className="sig-line" aria-hidden />
      <span className="sig-x" aria-hidden>✕ {empty ? label : ''}</span>
      <button type="button" className="btn btn-sm sig-clear" onClick={clear} disabled={empty}>Clear</button>
    </div>
  );
}
